import { useId, useState } from 'react';
import { ConfirmModal } from '../../../../components/ConfirmModal';

type Props = {
  /** Every record in the batch, across all pages and team names. */
  recordCount: number;
  /** How many of those are already marked JUCO. */
  markedCount: number;
  /**
   * True while any page is still being extracted. Those pages have no records yet, so "all pages"
   * would quietly skip them; the server refuses it too (409).
   */
  extracting: boolean;
  submitting: boolean;
  /** Runs after the reviewer confirms. The caller reports any failure. */
  onConfirm: (isJuco: boolean) => Promise<void>;
};

type PendingAction = 'mark' | 'unmark' | null;

const records = (count: number) => `${count} ${count === 1 ? 'record' : 'records'}`;

/**
 * Marks or unmarks every record in the batch as JUCO in one action.
 *
 * The per-page toggle stays the default because a junior-college scout book can hold a prep
 * program. This is for the common case where every team in the book is a junior college, which
 * otherwise meant toggling each of a dozen pages by hand.
 */
export const BatchWideJucoActions = ({
  recordCount,
  markedCount,
  extracting,
  submitting,
  onConfirm,
}: Props) => {
  const [pending, setPending] = useState<PendingAction>(null);
  const hintId = useId();

  const markDisabled =
    extracting || submitting || recordCount === 0 || markedCount === recordCount;
  const unmarkDisabled = extracting || submitting || markedCount === 0;

  const handleConfirm = async () => {
    if (!pending) return;
    try {
      await onConfirm(pending === 'mark');
    } finally {
      setPending(null);
    }
  };

  return (
    <>
      <div className="batch-review__juco" role="group" aria-label="JUCO for all pages">
        <button
          type="button"
          className="batch-review__juco-fill"
          disabled={markDisabled}
          aria-describedby={extracting ? hintId : undefined}
          onClick={() => setPending('mark')}
        >
          Mark all pages JUCO
        </button>
        <button
          type="button"
          className="batch-review__juco-fill"
          disabled={unmarkDisabled}
          aria-describedby={extracting ? hintId : undefined}
          onClick={() => setPending('unmark')}
        >
          Unmark all pages
        </button>
        {extracting && (
          <span id={hintId} className="batch-review__juco-hint">
            Available once every page finishes extracting.
          </span>
        )}
      </div>

      <ConfirmModal
        isOpen={pending !== null}
        onClose={() => setPending(null)}
        onConfirm={handleConfirm}
        busy={submitting}
        title={pending === 'mark' ? 'Mark all pages JUCO?' : 'Unmark all pages?'}
        message={
          pending === 'mark'
            ? `This marks all ${records(recordCount)} in this batch as JUCO, on every page and every team. Grad years that came from a player's class are recalculated.`
            : `This clears JUCO from ${records(markedCount)} in this batch, on every page and every team. Grad years that came from a player's class are recalculated.`
        }
        confirmText={pending === 'mark' ? 'Mark all pages' : 'Unmark all pages'}
      />
    </>
  );
};
